import { useEffect, useState } from 'react';
import { Trophy, X, Share2 } from 'lucide-react';
import { HexAccent } from './HexMotif';

/**
 * Triggers a celebratory overlay when a carrier hits a milestone.
 * Milestones come from the backend (via /api/hive/milestones/unseen).
 *
 * @param {object} milestone - { key, title, subtitle, icon_key, unlocked_at }
 * @param {function} onClose - called after user acknowledges
 */
const MilestoneCelebration = ({ milestone, onClose }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Auto-dismiss after 12 seconds if user ignores
    const t = setTimeout(() => handleClose(), 12000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  if (!milestone) return null;

  return (
    <div
      className={`fixed inset-0 z-[110] flex items-center justify-center p-4 transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      data-testid="milestone-celebration"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      {/* Confetti-style decorative hexes */}
      <HexAccent size={40} color="#D4AF37" className="absolute top-[15%] left-[20%] opacity-30 animate-pulse" />
      <HexAccent size={28} color="#F59E0B" className="absolute top-[25%] right-[15%] opacity-40 animate-pulse" style={{ animationDelay: '0.3s' }} />
      <HexAccent size={52} color="#10B981" className="absolute bottom-[20%] left-[18%] opacity-25 animate-pulse" style={{ animationDelay: '0.6s' }} />
      <HexAccent size={36} color="#3B82F6" className="absolute bottom-[25%] right-[22%] opacity-30 animate-pulse" style={{ animationDelay: '0.9s' }} />

      <div className="relative max-w-md w-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border-2 border-[#D4AF37] p-8 text-center">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 hover:bg-white/10"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-white/50" />
        </button>

        {/* Trophy with glow */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 bg-[#D4AF37]/30 rounded-full blur-2xl animate-pulse" />
          <div
            className="relative w-full h-full flex items-center justify-center border-2 border-[#D4AF37] bg-[#D4AF37]/10"
            style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
          >
            <Trophy className="w-10 h-10 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
        </div>

        {/* Badge label */}
        <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.3em] mb-2">
          Milestone Unlocked
        </p>

        <h2 className="font-['Barlow_Condensed'] text-3xl font-black text-white uppercase tracking-wide mb-2">
          {milestone.title}
        </h2>

        <p className="text-white/70 text-sm leading-relaxed mb-6">{milestone.subtitle}</p>

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#F5D47B]"
            data-testid="milestone-continue-btn"
          >
            Continue
          </button>
          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: `Queen B Logistics — ${milestone.title}`,
                  text: `I just hit a milestone with Queen B Logistics: ${milestone.title}! ${milestone.subtitle}`,
                });
              } else if (navigator.clipboard) {
                navigator.clipboard.writeText(`Just hit ${milestone.title} with Queen B Logistics 👑`);
              }
              handleClose();
            }}
            className="px-4 py-3 border border-white/20 text-white hover:bg-white/5"
            title="Share"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneCelebration;
